import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import React, { createContext, useContext, useRef } from 'react';
import DevicePlatformContext from './device-platform-context';
import accessToken from '../helpers/accessToken';

const applicationStartTime = performance.now();

const defaultValues = {
  startLoad: () => {},
  endLoad: () => {}
};

const SpanningLoadTimerContext = createContext(defaultValues);

const SpanningLoadTimerProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const isFirstStart = useRef(true);
  const startTime = useRef<number | null>(null);
  const { trackEvent } = useContext(AnalyticsContext);
  const devicePlatform = useContext(DevicePlatformContext);

  const startLoad = () => {
    if (startTime.current !== null) {
      datadogRum.addError(new Error('Load timer already started'));
    }
    startTime.current = performance.now();
    if (isFirstStart.current) {
      isFirstStart.current = false;
      const duration = startTime.current - applicationStartTime;
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      trackEvent('pp_application_load_time', {
        url: window.location.pathname,
        userId: accessToken?.().user.user_id,
        duration,
        metadata: attributesToSend,
        devicePlatform
      });
      datadogRum.addTiming('application_startup', startTime.current);
      datadogRum.addAction('application_startup', {
        duration
      });
    }
  };

  const endLoad = () => {
    if (startTime.current === null) {
      datadogRum.addError(new Error('Load timer not started'));
    } else {
      const endTime = performance.now();
      const duration = endTime - startTime.current;
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      datadogRum.addTiming('step_load_time', endTime);
      datadogRum.addAction('step_load_time', {
        duration
      });
      trackEvent('pp_load_time', {
        url: window.location.pathname,
        userId: accessToken?.().user.user_id,
        duration,
        metadata: attributesToSend,
        devicePlatform
      });
      startTime.current = null;
    }
  };

  return (
    <SpanningLoadTimerContext.Provider value={{ startLoad, endLoad }}>
      {children}
    </SpanningLoadTimerContext.Provider>
  );
};

export default SpanningLoadTimerContext;
export { SpanningLoadTimerProvider };
